import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

declare const google: any;

export interface GoogleAuthRequest {
  idToken: string;
  customerId?: string;
}

export interface GoogleAuthResponse {
  userId: string;
  email: string;
  name: string;
  isNewUser: boolean;
  succeeded: boolean;
  message: string;
  accessToken?: string;
  refreshToken?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAuthService {
  private readonly apiUrl = environment.apiUrls.authApiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Google OAuth ile backend'e kimlik doğrulama isteği gönder
   */
  authenticateWithGoogle(
    request: GoogleAuthRequest
  ): Observable<GoogleAuthResponse> {
    return this.http.post<GoogleAuthResponse>(
      `${this.apiUrl}/GoogleAuth`,
      request
    );
  }

  /**
   * Google Sign-In'i başlat
   */
  initializeGoogleSignIn(clientId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined') {
        google.accounts.id.initialize({
          client_id: clientId,
          callback: this.handleCredentialResponse.bind(this),
        });
        resolve();
      } else {
        // Google API'yi yükle
        this.loadGoogleAPI()
          .then(() => {
            if (typeof google !== 'undefined' && (google as any).accounts) {
              (google as any).accounts.id.initialize({
                client_id: clientId,
                callback: this.handleCredentialResponse.bind(this),
              });
            }
            resolve();
          })
          .catch(reject);
      }
    });
  }

  /**
   * Google Sign-In button'unu render et
   */
  renderGoogleSignInButton(elementId: string): void {
    if (typeof google !== 'undefined') {
      google.accounts.id.renderButton(document.getElementById(elementId), {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        shape: 'rectangular',
        text: 'signin_with',
        logo_alignment: 'left',
        width: 300,
      });
    }
  }

  /**
   * Google One Tap'i göster
   */
  showGoogleOneTap(): void {
    if (typeof google !== 'undefined') {
      google.accounts.id.prompt();
    }
  }

  /**
   * Google credential response'unu işle
   */
  private handleCredentialResponse(response: any): void {
    const idToken = response.credential;
    console.log('🔑 Google ID Token alındı:', idToken);

    // ID Token'ı backend'e gönder
    const authRequest: GoogleAuthRequest = {
      idToken,
      customerId: '72c54b1a-8e1c-45ea-8edd-b5da1091e325',
    };

    this.authenticateWithGoogle(authRequest).subscribe({
      next: (authResponse) => {
        console.log('✅ Google OAuth başarılı:', authResponse);

        if (authResponse.succeeded) {
          // Başarılı giriş - kullanıcıyı yönlendir
          this.handleSuccessfulLogin(authResponse);
        } else {
          console.error('❌ Google OAuth hatası:', authResponse.message);
          this.handleLoginError(authResponse.message);
        }
      },
      error: (error) => {
        console.error('❌ Google OAuth API hatası:', error);
        this.handleLoginError('Google ile giriş yaparken bir hata oluştu.');
      },
    });
  }

  /**
   * Başarılı giriş işlemini handle et
   */
  private handleSuccessfulLogin(response: GoogleAuthResponse): void {
    // Token'ları localStorage'a kaydet (opsiyonel)
    if (response.accessToken) {
      localStorage.setItem('accessToken', response.accessToken);
    }

    if (response.refreshToken) {
      localStorage.setItem('refreshToken', response.refreshToken);
    }

    // Kullanıcı bilgilerini kaydet
    localStorage.setItem(
      'user',
      JSON.stringify({
        id: response.userId,
        email: response.email,
        name: response.name,
        isNewUser: response.isNewUser,
      })
    );

    // Success mesajı göster
    alert(
      `🎉 Google OAuth Başarılı!\n\nKullanıcı: ${response.name}\nEmail: ${
        response.email
      }\nYeni Kullanıcı: ${response.isNewUser ? 'Evet' : 'Hayır'}`
    );

    // Dashboard'a yönlendir (isteğe bağlı)
    // window.location.href = '/settings';
  }

  /**
   * Giriş hatasını handle et
   */
  private handleLoginError(message: string): void {
    // Hata mesajını göster
    alert(`❌ Google OAuth Hatası:\n\n${message}`);
  }

  /**
   * Google API'yi dinamik olarak yükle
   */
  private loadGoogleAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google API yüklenemedi'));
      document.head.appendChild(script);
    });
  }

  /**
   * Manual olarak Google Sign-In'i tetikle
   */
  signInWithGoogle(clientId: string): void {
    this.initializeGoogleSignIn(clientId).then(() => {
      // Programmatik olarak sign-in'i tetikle
      if (typeof google !== 'undefined') {
        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('One Tap gösterilemedi, popup açılıyor...');
          }
        });
      }
    });
  }
}
